/**
 * CaptchaChallenge — AWS seviyesi CAPTCHA
 * Büyük/küçük harf + rakam, çok bozuk font, gürültü çizgileri, nokta pattern
 * AI okunamaz — insanlar zor okur
 */
import { RefreshCw } from 'lucide-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

interface Props { onVerified: (ok: boolean) => void }

function genCode(): string {
  // Karışık: büyük harf + küçük harf + rakam (benzer karakterler hariç: 0O, 1lI)
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let c = ''
  for (let i = 0; i < 6; i++) c += chars[Math.floor(Math.random() * chars.length)]
  return c
}

function draw(canvas: HTMLCanvasElement, code: string) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const w = canvas.width, h = canvas.height

  // Arka plan — koyu, hafif gürültülü
  ctx.fillStyle = '#0a0e17'
  ctx.fillRect(0, 0, w, h)

  // Arka plan gürültü — rastgele renkli küçük kareler
  for (let i = 0; i < 200; i++) {
    ctx.fillStyle = `rgba(${50 + Math.random() * 80},${50 + Math.random() * 80},${80 + Math.random() * 120},${Math.random() * 0.25})`
    const s = 1 + Math.random() * 2
    ctx.fillRect(Math.random() * w, Math.random() * h, s, s)
  }

  // Gürültü çizgileri — eğri, kalın, yarı saydam
  for (let i = 0; i < 6; i++) {
    ctx.beginPath()
    ctx.moveTo(Math.random() * w, Math.random() * h)
    ctx.bezierCurveTo(
      Math.random() * w, Math.random() * h,
      Math.random() * w, Math.random() * h,
      Math.random() * w, Math.random() * h
    )
    ctx.strokeStyle = `rgba(${60 + Math.random() * 100},${60 + Math.random() * 100},${100 + Math.random() * 155},${Math.random() * 0.35 + 0.1})`
    ctx.lineWidth = Math.random() * 2.5 + 0.5
    ctx.stroke()
  }

  // Karakterleri çiz — her biri farklı açı, boyut, renk, font, y-offset
  const colors = ['#818cf8', '#c084fc', '#f472b6', '#34d399', '#fbbf24', '#60a5fa', '#fb923c', '#a78bfa']
  const fonts = ['monospace', 'serif', 'sans-serif', 'Georgia', 'Courier New', 'Impact']
  const startX = 12
  const spacing = (w - 24) / code.length

  for (let i = 0; i < code.length; i++) {
    ctx.save()
    const x = startX + i * spacing + (Math.random() - 0.5) * 6
    const y = h / 2 + (Math.random() - 0.5) * 14
    const angle = (Math.random() - 0.5) * 0.7 // -0.35 ~ 0.35 rad
    const size = 18 + Math.random() * 10
    const font = fonts[Math.floor(Math.random() * fonts.length)]
    const bold = Math.random() > 0.4 ? 'bold' : 'normal'
    const italic = Math.random() > 0.6 ? 'italic ' : ''

    ctx.translate(x, y)
    ctx.rotate(angle)
    // Skew — eğme efekti
    ctx.transform(1, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, 1, 0, 0)

    ctx.font = `${italic}${bold} ${size}px ${font}`
    ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)]

    // Gölge
    ctx.shadowColor = 'rgba(0,0,0,0.6)'
    ctx.shadowBlur = 2 + Math.random() * 3
    ctx.shadowOffsetX = Math.random() * 2
    ctx.shadowOffsetY = Math.random() * 2
    ctx.fillText(code[i], 0, 0)

    // Üstüne kesici çizgi (harfi böl)
    if (Math.random() > 0.3) {
      ctx.beginPath()
      const ly = (Math.random() - 0.5) * size * 0.6
      ctx.moveTo(-4, ly)
      ctx.lineTo(size * 0.6, ly + (Math.random() - 0.5) * 6)
      ctx.strokeStyle = ctx.fillStyle
      ctx.globalAlpha = 0.35
      ctx.lineWidth = 1 + Math.random()
      ctx.stroke()
      ctx.globalAlpha = 1
    }

    ctx.restore()
  }

  // Üst katman gürültü — ince çizgiler
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.moveTo(0, Math.random() * h)
    ctx.lineTo(w, Math.random() * h)
    ctx.strokeStyle = `rgba(255,255,255,${Math.random() * 0.06 + 0.02})`
    ctx.lineWidth = 1
    ctx.stroke()
  }
}

function CaptchaChallenge({ onVerified }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [code, setCode] = useState(genCode)
  const [input, setInput] = useState('')
  const [error, setError] = useState(false)
  const [verified, setVerified] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const MAX_ATTEMPTS = 4

  const refresh = useCallback(() => {
    const c = genCode()
    setCode(c)
    setInput('')
    setError(false)
    setVerified(false)
    setAttempts(0)
    onVerified(false)
  }, [onVerified])

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current, code)
  }, [code])

  const handleChange = (val: string) => {
    if (verified) return
    const clean = val.slice(0, 6)
    setInput(clean)
    setError(false)
    if (clean.length === 6) {
      if (clean === code) {
        setVerified(true)
        onVerified(true)
      } else {
        setError(true)
        onVerified(false)
        const newAttempts = attempts + 1
        setAttempts(newAttempts)
        // 4 denemeden sonra yeni kod üret
        if (newAttempts >= MAX_ATTEMPTS) {
          setTimeout(refresh, 1200)
        }
        // Input SİLİNMEZ — kullanıcı yanlış harfi kendisi düzeltsin
      }
    } else {
      onVerified(false)
    }
  }

  return (
    <div style={{ marginBottom: 'clamp(6px, 0.8vmin, 10px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <canvas ref={canvasRef} width={200} height={52}
          style={{
            borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
            flexShrink: 0, imageRendering: 'pixelated',
          }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          <input
            type="text" maxLength={6} autoComplete="off" spellCheck={false}
            value={input} onChange={e => handleChange(e.target.value)}
            placeholder="Kodu gir"
            disabled={verified}
            style={{
              width: '100%', padding: '8px 10px', textAlign: 'center', letterSpacing: 3,
              background: verified ? 'rgba(16,185,129,0.1)' : error ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${verified ? 'rgba(16,185,129,0.4)' : error ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8, color: verified ? '#10b981' : '#fff',
              fontSize: 15, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
            }}
          />
          {verified && <div style={{ fontSize: 10, color: '#10b981', textAlign: 'center' }}>✓ Doğrulandı</div>}
          {error && <div style={{ fontSize: 10, color: '#ef4444', textAlign: 'center' }}>✗ Yanlış ({MAX_ATTEMPTS - attempts} hak kaldı)</div>}
        </div>
        <button onClick={refresh} title="Yeni kod" style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)',
          cursor: 'pointer', padding: 4, flexShrink: 0,
        }}><RefreshCw size={14} /></button>
      </div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4, textAlign: 'center' }}>
        Büyük/küçük harf duyarlı · 6 karakter
      </div>
    </div>
  )
}

export default memo(CaptchaChallenge)
